/**
 * @fileoverview Investigation Windows Panel
 *
 * Renders the recovery / investigation lookback windows for one job
 * as a dedicated table — deliberately **not** on the calendar
 * because these are date RANGES (relation-back date − period), not
 * point-in-time events, and mixing them with reporting deadlines
 * confused the calendar surface.
 *
 * Backed by `keyDates.queries.listInvestigationWindowsForJob`.
 * Renders Corporations Act windows (s588FA / FB / FC / FDA / FDB /
 * FE / FJ / FL) for windings up with a relation-back day, or —
 * for a bankruptcy job — the Bankruptcy Act 1966 windows (s 120 /
 * s 121 / s 122) anchored on the s 115 commencement calculator
 * (MOR-661). The `regime` discriminant on the query response drives
 * which anchor chips and legislation text are shown; a Part X personal
 * insolvency agreement (`part_x`, MOR-1838) has no claw-back windows
 * under either Act, so it only ever shows the note. When a corporate
 * type has no applicable / yet-known relation-back day (receivership,
 * scheme, pre-liquidation VA/DOCA/SBR/provisional, or a court
 * liquidation awaiting its winding-up application date), or a
 * bankruptcy has no date of bankruptcy recorded, the panel shows an
 * explanatory note instead of windows.
 */

import { Fragment, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { format, isValid, parseISO } from "date-fns";
import { ChevronRight, Scale, Settings } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { APPLIED_RELATION_BACK_CALLOUT } from "@cvx/keyDates/relationBackCopy";

import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Spinner } from "@/ui/primitives/spinner";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/primitives/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";

interface InvestigationWindowsPanelProps {
  jobId: Id<"jobs">;
}

export function InvestigationWindowsPanel({
  jobId,
}: InvestigationWindowsPanelProps) {
  const { data } = useQuery(
    convexQuery(api.keyDates.queries.listInvestigationWindowsForJob, {
      jobId,
    }),
  );

  const grouped = useMemo(() => {
    if (!data) return null;
    const requires: typeof data.windows = [];
    const noRequires: typeof data.windows = [];
    for (const window of data.windows) {
      if (window.requiresInsolvency) requires.push(window);
      else noRequires.push(window);
    }
    return { requires, noRequires };
  }, [data]);

  const actLabel =
    data?.regime === "bankruptcy" || data?.regime === "part_x"
      ? "Bankruptcy Act 1966 (Cth)"
      : "Corporations Act 2001 (Cth)";

  if (data === undefined) {
    return (
      <Card>
        <CardContent className="flex h-32 items-center justify-center">
          <Spinner className="text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // No windows. If we have an explanatory note (corporate type without an
  // applicable / yet-known relation-back day, or a Part X arrangement), surface
  // it so the absence of windows is intelligible rather than silent. Otherwise
  // (bankruptcy, etc.) hide the panel entirely as before.
  if (data.windows.length === 0) {
    if (!data.relationBackNote) return null;
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Scale className="size-4 text-muted-foreground" />
            <CardTitle className="text-base">
              Investigation & recovery review windows
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col items-start gap-3">
          <p className="text-sm text-muted-foreground">
            {formatDatesInCopy(data.relationBackNote)}
          </p>
          {data.needsWindingUpApplicationDate && (
            <Button asChild variant="outline" size="sm" className="gap-1.5">
              <Link
                to="/dashboard/jobs/$jobId/settings"
                params={{ jobId }}
                search={{ tab: "appointment" }}
              >
                <Settings className="size-3.5" />
                Enter Winding Up Application date in Settings
              </Link>
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Scale className="size-4 text-muted-foreground" />
          <CardTitle className="text-base">
            Investigation & recovery review windows
          </CardTitle>
        </div>
        <div className="flex flex-wrap gap-2 pt-2 text-xs text-muted-foreground">
          {data.regime === "bankruptcy" ? (
            <>
              {data.bankruptcyCommencementDate && (
                <AnchorChip
                  label="Commencement of bankruptcy"
                  iso={data.bankruptcyCommencementDate}
                />
              )}
              {/* The s 115 pathway that produced the commencement. */}
              {data.bankruptcyCommencementSection && (
                <span className="rounded bg-secondary px-1.5 py-0.5 font-medium">
                  {data.bankruptcyCommencementSection}
                </span>
              )}
              {data.dateOfBankruptcy && (
                <AnchorChip
                  label="Date of bankruptcy"
                  iso={data.dateOfBankruptcy}
                />
              )}
              {data.petitionPresentedDate && (
                <AnchorChip
                  label="Petition presented"
                  iso={data.petitionPresentedDate}
                />
              )}
            </>
          ) : (
            <>
              {data.relationBackDate && (
                <AnchorChip
                  label="Relation-back"
                  iso={data.relationBackDate}
                  assumed={data.relationBackAssumed}
                />
              )}
              {/* MOR-901 — the statutory rule that produced the anchor. */}
              {data.relationBackSection && (
                <span className="rounded bg-secondary px-1.5 py-0.5 font-medium">
                  {data.relationBackSection}
                </span>
              )}
              {data.relationBackIsOverride && (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
                  Relation-back date manually overridden
                </span>
              )}
              {data.appointmentDate && (
                <AnchorChip label="Appointment" iso={data.appointmentDate} />
              )}
              {data.commencementDate && (
                <AnchorChip label="Commencement" iso={data.commencementDate} />
              )}
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-8 p-0 pb-2">
        {data.relationBackAssumed && (
          <div className="mx-4 mt-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
            {data.relationBackNote
              ? formatDatesInCopy(data.relationBackNote)
              : APPLIED_RELATION_BACK_CALLOUT}
          </div>
        )}
        {grouped && grouped.requires.length > 0 && (
          <WindowsTable
            heading="Recoveries Requiring Insolvency to be Proven"
            rows={grouped.requires}
            actLabel={actLabel}
          />
        )}
        {grouped && grouped.noRequires.length > 0 && (
          <WindowsTable
            heading="Recoveries Not Requiring Insolvency to be Proven"
            rows={grouped.noRequires}
            actLabel={actLabel}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/**
 * Wording of the relevant Corporations Act 2001 (Cth) sections,
 * keyed by the short section label shown in the table badge.
 */
const LEGISLATION_TEXT: Record<string, string> = {
  s588FA: `Section 588FA — Unfair preferences

(1) A transaction is an unfair preference given by a company to a creditor of the company if, and only if:
  (a) the company and the creditor are parties to the transaction (even if someone else is also a party); and
  (b) the transaction results in the creditor receiving from the company, in respect of an unsecured debt that the company owes to the creditor, more than the creditor would receive from the company in respect of the debt if the transaction were set aside and the creditor were to prove for the debt in a winding up of the company;

even if the transaction is entered into, is given effect to, or is required to be given effect to, because of an order of an Australian court or a direction by an agency.`,

  s588FB: `Section 588FB — Uncommercial transactions

(1) A transaction of a company is an uncommercial transaction of the company if, and only if, it may be expected that a reasonable person in the company's circumstances would not have entered into the transaction, having regard to:
  (a) the benefits (if any) to the company of entering into the transaction; and
  (b) the detriment to the company of entering into the transaction; and
  (c) the respective benefits to other parties to the transaction of entering into it; and
  (d) any other relevant matter.`,

  s588FC: `Section 588FC — Insolvent transactions — Loss to Related Entities

(1) A transaction of a company is an insolvent transaction of the company if, and only if:
  (a) it is an unfair preference given by the company, or an uncommercial transaction of the company; and
  (b) any of the following happens:
    (i) it is entered into, or an act is done for the purposes of giving effect to it, during the 6 months ending on the relation-back day;
    (ii) it is entered into, or an act is done for the purposes of giving effect to it, during the period beginning 6 months and ending 4 years before the relation-back day, and the company and a related entity of the company are parties to it;
    (iii) it is entered into, or an act is done for the purposes of giving effect to it, during the period beginning 6 months and ending 2 years before the relation-back day, and a person who was at the time a related entity of the company benefited under the transaction; and
  (c) the company was insolvent at that time, or became insolvent because of entering into the transaction.`,

  "s588FE(5)": `Section 588FE(5) — Transactions to Defeat Creditors

(5) The transaction is voidable if:
  (a) it was entered into, or an act was done for the purposes of giving effect to it:
    (i) during the 10 years ending on the relation-back day; or
    (ii) after that day but on or before the day when the winding up began; and
  (b) the company was a party to the transaction for the purpose, or for purposes including the purpose, of:
    (i) defeating the interests of the company's existing or future creditors; or
    (ii) giving to a person other than the company a benefit or advantage.`,

  s588FDA: `Section 588FDA — Unreasonable director-related transactions

(1) A transaction of a company is an unreasonable director-related transaction of the company if, and only if:
  (a) the transaction is a payment, conveyance or other disposition of property of the company; and
  (b) the payment, conveyance or disposition is made to:
    (i) a director of the company; or
    (ii) a close associate of a director of the company; or
    (iii) a person on behalf of, or for the benefit of, a person mentioned in subparagraph (i) or (ii); and
  (c) it may be expected that a reasonable person in the company's circumstances would not have entered into the transaction, having regard to:
    (i) the benefits (if any) to the company of entering into the transaction; and
    (ii) the detriment to the company of entering into the transaction; and
    (iii) the respective benefits to other parties to the transaction of entering into it; and
    (iv) any other relevant matter.

Note: Does not require proof of insolvency at the time of the transaction.`,

  s588FDB: `Section 588FDB — Creditor-defeating dispositions

(1) A disposition of property of a company is a creditor-defeating disposition if:
  (a) the consideration payable to the company for the disposition was less than the lesser of:
    (i) the market value of the property; or
    (ii) the best price that was reasonably obtainable for the property, having regard to the circumstances existing at the time of the disposition; and
  (b) the disposition has the effect of:
    (i) preventing the property from becoming available for the benefit of the company's creditors in the winding up of the company; or
    (ii) hindering, or significantly delaying, the process of making the property available for the benefit of the company's creditors in the winding up of the company.

Note: Targets illegal phoenix activity. Does not require proof of insolvency.`,

  s588FJ: `Section 588FJ — Circulating security interests created within 6 months before the relation-back day

(1) A circulating security interest that a company has created is void (so far as it secures any thing in respect of which the company was the grantor) against the company's liquidator unless:
  (a) it secures a market value advance; or
  (b) it is created at or before the beginning of the period of 6 months ending on the relation-back day.

(2) In this section, a security interest secures a market value advance if it secures:
  (a) the payment of a debt that the company incurred at a time when the security interest was already in force; and
  (b) the value of the asset to which the security interest relates (at the time the debt was incurred) was not significantly greater than the amount of the debt.`,

  s588FL: `Section 588FL — PPSA security interests vesting in grantor

(1) A PPSA security interest in property of a company vests in the company if:
  (a) the security interest is unperfected at the critical time; and
  (b) the company is wound up, or an administrator of the company is appointed, after the critical time.

(2) The critical time is:
  (a) if an order is made to wind up the company, or the company resolves to wind itself up, within 6 months after an administrator of the company is first appointed — the beginning of the day on which the administrator is first appointed; or
  (b) otherwise — the beginning of the day on which the order is made, or the resolution is passed.

Note: A security interest that is not registered on the PPSR within 20 business days of the security agreement is unperfected and vests in the company.`,

  "s 120": `Section 120 — Undervalued transactions

(1) A transfer of property by a person who later becomes a bankrupt (the transferor) to another person (the transferee) is void against the trustee in the transferor's bankruptcy if:
  (a) the transfer took place in the period beginning 5 years before the commencement of the bankruptcy and ending on the date of the bankruptcy; and
  (b) the transferee gave no consideration for the transfer or gave consideration of less value than the market value of the property.

(3) Despite subsection (1), a transfer is not void against the trustee if:
  (a) in the case of a transfer to a related entity of the transferor — the transfer took place more than 4 years before the commencement of the bankruptcy; or
  (b) in any other case — the transfer took place more than 2 years before the commencement of the bankruptcy;
and the transferee proves that the transferor was solvent at the time of the transfer.`,

  "s 121": `Section 121 — Transfers to defeat creditors

(1) A transfer of property by a person who later becomes a bankrupt (the transferor) to another person (the transferee) is void against the trustee in the transferor's bankruptcy if:
  (a) the property would probably have become part of the transferor's estate or would probably have been available to creditors if the property had not been transferred; and
  (b) the transferor's main purpose in making the transfer was:
    (i) to prevent the transferred property from becoming divisible among the transferor's creditors; or
    (ii) to hinder or delay the process of making property available for division among the transferor's creditors.

(2) The transferor's main purpose in making a transfer is taken to be the purpose described in paragraph (1)(b) if it can reasonably be inferred from all the circumstances that, at the time of the transfer, the transferor was, or was about to become, insolvent.

Note: There is no fixed look-back period — any transfer on or before the date of bankruptcy is in scope where the main purpose test is satisfied.`,

  "s 122": `Section 122 — Preferences

(1) A transfer of property by a person who is insolvent (the debtor) in favour of a creditor is void against the trustee in the debtor's bankruptcy if the transfer:
  (a) had the effect of giving the creditor a preference, priority or advantage over other creditors; and
  (b) was made in the period beginning 6 months before the presentation of the petition and ending on the date of the bankruptcy.

(1)(b) item 2: where a debtor's petition was presented while a creditor's petition was pending, the period begins on the commencement of the bankruptcy.

(2) Subsection (1) does not apply to a transfer made in favour of a creditor where the transfer was made in good faith and in the ordinary course of business, and for market value.

Note: The trustee must prove the debtor was insolvent at the time of each transfer.`,
};

function AnchorChip({
  label,
  iso,
  assumed,
}: {
  label: string;
  iso: string;
  assumed?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2 py-0.5">
      <span className="font-medium text-foreground">{label}:</span>
      <span className="tabular-nums">{formatDateLabel(iso)}</span>
      {assumed && (
        <Badge
          variant="outline"
          size="sm"
          className="border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200"
        >
          Assumed
        </Badge>
      )}
    </span>
  );
}

function WindowsTable({
  heading,
  rows,
  actLabel,
}: {
  heading: string;
  actLabel: string;
  rows: ReadonlyArray<{
    code: string;
    title: string;
    description: string;
    section: string;
    anchorLabel: string;
    periodLabel: string;
    reviewStartDate: string | null;
    reviewEndDate: string;
  }>;
}) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  return (
    <div>
      <h3 className="border-b px-4 py-3 text-sm font-semibold text-foreground">
        {heading}
      </h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead className="w-[260px]">Transaction type</TableHead>
            <TableHead className="w-[100px]">Section</TableHead>
            <TableHead className="w-[120px]">Period</TableHead>
            <TableHead className="w-[140px]">Review start</TableHead>
            <TableHead className="w-[140px]">Review end</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const isExpanded = expandedRow === row.code;
            const legislationText = LEGISLATION_TEXT[row.section];
            return (
              <Fragment key={row.code}>
                <TableRow
                  className="cursor-pointer"
                  onClick={() => setExpandedRow(isExpanded ? null : row.code)}
                >
                  <TableCell className="w-8 px-2">
                    <ChevronRight
                      className={`size-4 text-muted-foreground transition-transform ${
                        isExpanded ? "rotate-90" : ""
                      }`}
                    />
                  </TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help font-medium underline decoration-dotted underline-offset-2">
                            {stripSuffix(row.title, " review window starts")
                              .replace(/ review window$/, "")
                              .replace(/ review starts$/, "")}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="right"
                          className="max-w-xs text-xs"
                        >
                          {row.description}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" size="sm">
                      {row.section}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {row.periodLabel}
                    <div className="text-[10px] opacity-70">
                      from {row.anchorLabel.toLowerCase()}
                    </div>
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {row.reviewStartDate ? (
                      formatDateLabel(row.reviewStartDate)
                    ) : (
                      <span className="text-muted-foreground">No floor</span>
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {formatDateLabel(row.reviewEndDate)}
                  </TableCell>
                </TableRow>
                {isExpanded && legislationText && (
                  <TableRow key={`${row.code}-expanded`}>
                    <TableCell colSpan={6} className="bg-muted/50 p-0">
                      <div className="px-6 py-4">
                        <p className="mb-1 text-xs font-semibold text-foreground">
                          {actLabel}
                        </p>
                        <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed text-muted-foreground">
                          {legislationText}
                        </pre>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function formatDateLabel(iso: string): string {
  try {
    return format(parseISO(iso), "d MMM yyyy");
  } catch {
    return iso;
  }
}

/** ISO `yyyy-MM-dd` tokens in explanatory copy → `dd/MM/yyyy`. */
function formatDatesInCopy(copy: string): string {
  return copy.replace(/\b\d{4}-\d{2}-\d{2}\b/g, (iso) => {
    const parsed = parseISO(iso);
    return isValid(parsed) ? format(parsed, "dd/MM/yyyy") : iso;
  });
}

function stripSuffix(value: string, suffix: string): string {
  return value.endsWith(suffix) ? value.slice(0, -suffix.length) : value;
}
